import { useQueryClient } from "@tanstack/react-query";
import { useEffect, type ReactNode } from "react";
import { Navigate, useLocation, useNavigate } from "react-router";
import { ApiError, UNAUTHORISED_EVENT } from "../../lib/api";
import { ConfigProvider } from "../../lib/config";
import { useConfigQuery, useMe } from "../../lib/queries";
import { ErrorNote, Loading } from "../../components/ui";

/** Requires a session, then loads configuration before rendering the app. */
export function AuthGate({ children }: { children: ReactNode }) {
  const me = useMe();
  const config = useConfigQuery(!!me.data);
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();

  useEffect(() => {
    const onUnauthorised = () => {
      qc.clear();
      navigate(`/sign-in?next=${encodeURIComponent(location.pathname + location.search)}`);
    };
    window.addEventListener(UNAUTHORISED_EVENT, onUnauthorised);
    return () => window.removeEventListener(UNAUTHORISED_EVENT, onUnauthorised);
  }, [qc, navigate, location]);

  if (me.isLoading) return <Splash />;
  if (me.error) {
    if (me.error instanceof ApiError && me.error.status === 401)
      return <Navigate to={`/sign-in?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
    return (
      <div className="mx-auto max-w-md p-8">
        <ErrorNote error={me.error} />
      </div>
    );
  }
  if (config.isLoading || !config.data) return config.error ? <ErrorNote error={config.error} className="m-8" /> : <Splash />;
  return <ConfigProvider bundle={config.data}>{children}</ConfigProvider>;
}

function Splash() {
  return (
    <div className="grid min-h-screen place-items-center bg-cream">
      <Loading label="Opening the hub…" />
    </div>
  );
}
